import * as React from 'react';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
// @mui
import {
  Box, Modal, Button, Stack, TextField, Checkbox,
  Typography, Autocomplete, Divider, Alert
} from '@mui/material';
import GppGoodIcon from '@mui/icons-material/GppGood';

import { updateCliente } from './api';
import { cores, labelCampoEscuro } from '../styles/styleUtils';
import MaskTelefone from './helpers/MaskTelefone';
import MaskCpfCnpj from './helpers/MaskCpfCnpj';
import config from '../config.json';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '55vw',
  maxHeight: '92vh',
  '@media screen and (max-width: 600px)': {
    width: '95vw',
    maxHeight: '95vh',
  },
  bgcolor: 'transparent',
  border: `1px solid ${cores.chumbo}`, borderTop: `4px solid ${cores.laranja}`,
  boxShadow: 24,
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'auto',
  background: `linear-gradient(180deg, ${cores.painelTopo} 0%, ${cores.painelBase} 100%)`,
  alignItems: 'center',
  justifyContent: 'flex-start',
};

const sexos = ['Masculino', 'Feminino'];

// Finalidades de uso dos dados — LGPD Art. 7º
const finalidadesDados = [
  'Proposta comercial',
  'Emissão de nota fiscal',
  'Contrato de instalação',
];

// ----------------------------------------------------------------------

const ModalAddCliente = React.forwardRef((props, ref) => {
  const [cliente, setCliente] = useState(props.produto);
  const [estadosEndpoint] = useState('https://servicodados.ibge.gov.br/api/v1/localidades/estados');
  const [estados, setEstados] = useState([]);
  const [cidades, setCidades] = useState([]);

  // LGPD — estado do consentimento
  const [lgpdConsentimento, setLgpdConsentimento] = useState(false);
  const [lgpdFinalidade, setLgpdFinalidade] = useState(null);

  // Estados e Cidades
  useEffect(() => {
    fetch(estadosEndpoint)
      .then(response => {
        if (!response.ok) throw new Error('Erro na solicitação');
        return response.json();
      })
      .then(estados => {
        const estadosOrdenados = estados.sort((a, b) => a.nome.localeCompare(b.nome));
        setEstados(estadosOrdenados);
      })
      .catch(error => console.error('Erro ao obter os estados:', error));
  }, [cliente?.estado]);

  useEffect(() => {
    if (cliente?.estado) {
      fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${cliente.estado.sigla}/municipios`)
        .then(response => {
          if (!response.ok) throw new Error('Erro na solicitação');
          return response.json();
        })
        .then(cidades => {
          const cidadesOrdenados = cidades.sort((a, b) => a.nome.localeCompare(b.nome));
          setCidades(cidadesOrdenados);
        })
        .catch(error => console.error('Erro ao obter os estados:', error));
    }
  }, [cliente?.estado]);

  useEffect(() => {
    if (props.produto) {
      setCliente({ ativo: true, ...props.produto });
      // Preencher campos LGPD se já existirem (edição de cliente)
      setLgpdConsentimento(props.produto?.lgpd?.consentimento || false);
      setLgpdFinalidade(props.produto?.lgpd?.finalidade || null);
    } else {
      setCliente({ ativo: true });
      setLgpdConsentimento(false);
      setLgpdFinalidade(null);
    }
  }, [props.produto]);

  const handleChange = (e) => {
    setCliente({ ...cliente, [e.target.id]: e.target.value });
  };

  const handleChangeCpf = (e) => {
    const newCpf = e.target.value.replace(/\D/g, '');
    setCliente({ ...cliente, cpf: newCpf });
  };

  const salvaCliente = async () => {
    // Validação LGPD — bloquear salvamento sem consentimento
    if (!lgpdConsentimento) {
      window.alert('Para cadastrar o cliente, é necessário declarar que ele foi informado e autorizou o uso de seus dados conforme a LGPD.');
      return;
    }
    if (!lgpdFinalidade) {
      window.alert('Selecione a finalidade do uso dos dados do cliente.');
      return;
    }

    // Usuário logado que está declarando o consentimento
    const usuarioLogado = JSON.parse(localStorage.getItem('userData'));

    // Montar objeto LGPD
    const lgpdData = {
      consentimento: true,
      finalidade: lgpdFinalidade,
      dataConsentimento: new Date().toISOString(),
      declaradoPor: usuarioLogado?.nome || 'Sistema',
      dataRetencao: new Date(
        new Date().setFullYear(new Date().getFullYear() + 5)
      ).toISOString(), // 5 anos a partir do cadastro
    };

    if (cliente) {
      cliente.tipoCliente = cliente.cpf?.length > 11 ? 'J' : 'F';
      cliente.lgpd = lgpdData;
    }

    const resultado = await updateCliente(cliente);
    if (!resultado.ok) {
      window.alert(resultado.msg);
      return;
    }
    props.onConfirm(resultado.data.newCliente);
    props.onClose();
  };

  const onClose = () => {
    props.onClose();
  };

  // Verifica se é edição (cliente já tem LGPD registrado)
  const jaTemConsentimento = props.produto?.lgpd?.consentimento;

  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <Box sx={style} gap={2}>
        <TextField
          id="nome"
          label="Nome"
          variant="outlined"
          value={cliente?.nome}
          onChange={handleChange}
          sx={{ width: '70%', backgroundColor: 'white', ...labelCampoEscuro, mt: 1 }}
        />
        <MaskCpfCnpj
          id="cpf"
          label="CPF/CNPJ"
          variant="outlined"
          value={cliente?.cpf}
          onChange={handleChangeCpf}
          sx={{ width: '70%', backgroundColor: 'white', ...labelCampoEscuro }}
        />
        <MaskTelefone
          id="telefone"
          label="Telefone"
          variant="outlined"
          value={cliente?.telefone}
          InputLabelProps={{ shrink: true }}
          onChange={handleChange}
          sx={{ width: '70%', backgroundColor: 'white', ...labelCampoEscuro }}
        />
        <Autocomplete
          disablePortal
          id="sexo"
          value={cliente?.sexo || ''}
          onChange={(event, newValue) => {
            setCliente({ ...cliente, sexo: newValue });
          }}
          options={sexos}
          isOptionEqualToValue={(option, value) => option === value}
          getOptionLabel={(option) => `${option}`}
          sx={{ width: '70%', backgroundColor: 'white', ...labelCampoEscuro }}
          renderInput={(params) => <TextField {...params} label="Selecione o gênero" />}
        />

        <Stack direction={'row'} gap={2} sx={{ width: '70%' }}>
          <Autocomplete
            disablePortal
            id="estado"
            value={cliente?.estado || null}
            options={estados}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.nome} - ${option.sigla}`}
            sx={{ width: 300, backgroundColor: 'white', ...labelCampoEscuro }}
            renderInput={(params) => <TextField {...params} label="Estado" />}
            onChange={(event, newValue) => {
              setCliente((prevCliente) => ({ ...prevCliente, estado: newValue }));
            }}
          />
          <Autocomplete
            disablePortal
            id="cidade"
            value={cliente?.cidade || null}
            options={cidades}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => `${option.nome}`}
            sx={{ width: '100%', backgroundColor: 'white', ...labelCampoEscuro }}
            renderInput={(params) => <TextField {...params} label="Cidade" />}
            onChange={(event, newValue) => {
              setCliente((prevCliente) => ({ ...prevCliente, cidade: newValue }));
            }}
          />
        </Stack>

        <Stack direction={'row'} gap={2} sx={{ width: '70%' }}>
          <TextField
            id="nascimento"
            type="date"
            label="Nascimento/Fundação"
            variant="outlined"
            value={
              cliente?.nascimento
                ? new Date(cliente.nascimento).toISOString().split('T')[0]
                : ''
            }
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 300, backgroundColor: 'white', ...labelCampoEscuro, mt: 1 }}
          />
          <TextField
            id="email"
            label="Email"
            variant="outlined"
            value={cliente?.email}
            onChange={handleChange}
            sx={{ width: '100%', backgroundColor: 'white', ...labelCampoEscuro, mt: 1 }}
          />
        </Stack>

        <Stack direction={'row'}>
          <Checkbox
            checked={!!cliente?.ativo}
            onChange={() => setCliente((prev) => ({ ...(prev || {}), ativo: !prev?.ativo }))}
            inputProps={{ 'aria-label': 'controlled' }}
            sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: cores.laranja } }}
          />
          <Typography mt={1} sx={{ color: cores.textoClaro }}>Ativo</Typography>
        </Stack>

        {/* ===== SEÇÃO LGPD ===== */}
        <Divider sx={{ width: '70%', my: 1, borderColor: 'rgba(255,255,255,0.2)' }} />

        <Stack sx={{ width: '70%' }} gap={1}>
          <Stack direction="row" alignItems="center" gap={1}>
            <GppGoodIcon sx={{ color: cores.laranja, fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ color: cores.textoClaro, fontWeight: 600 }}>
              Conformidade LGPD
            </Typography>
          </Stack>

          {/* Exibir info se já tem consentimento registrado */}
          {jaTemConsentimento && (
            <Alert severity="success" sx={{ fontSize: 12, py: 0 }}>
              Consentimento já registrado em{' '}
              {new Date(props.produto.lgpd.dataConsentimento).toLocaleDateString('pt-BR')}
              {' '}por {props.produto.lgpd.declaradoPor}
            </Alert>
          )}

          {/* Finalidade */}
          <Autocomplete
            disablePortal
            value={lgpdFinalidade}
            onChange={(event, newValue) => setLgpdFinalidade(newValue)}
            options={finalidadesDados}
            isOptionEqualToValue={(option, value) => option === value}
            getOptionLabel={(option) => option}
            sx={{ backgroundColor: 'white', ...labelCampoEscuro }}
            renderInput={(params) => (
              <TextField {...params} label="Finalidade do uso dos dados *" size="small" />
            )}
          />

          {/* Checkbox de consentimento */}
          <Stack direction="row" alignItems="flex-start" gap={1}>
            <Checkbox
              checked={lgpdConsentimento}
              onChange={() => setLgpdConsentimento(!lgpdConsentimento)}
              sx={{ color: cores.laranja, '&.Mui-checked': { color: cores.laranja }, pt: 0.5 }}
              size="small"
            />
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.4, mt: 0.5 }}>
              Declaro que o cliente foi informado sobre o uso de seus dados pessoais
              conforme a <strong>Lei Geral de Proteção de Dados (LGPD)</strong> e
              autorizou expressamente o cadastro e uso para a finalidade indicada.
            </Typography>
          </Stack>

          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)' }}>
            * Os dados serão retidos por 5 anos a partir do cadastro, conforme política de retenção da empresa.
          </Typography>
        </Stack>
        {/* ===== FIM LGPD ===== */}

        <Stack direction={'row'} justifyContent={'space-between'} gap={20} mt={1}>
          <Button variant="outlined" onClick={onClose} color="error">
            Cancelar
          </Button>
          <Button variant="contained" color="success" onClick={salvaCliente}>
            Salvar
          </Button>
        </Stack>
      </Box>
    </Modal>
  );
});

ModalAddCliente.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
};

export default ModalAddCliente;